"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReturnsClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const core_auth_1 = require("@deliverr/core-auth");
const client_auth_1 = require("@deliverr/client-auth");
const RETURNS_BASE_URL = process.env.RETURNS_BASE_URL;
class ReturnsClient extends base_client_1.ApiClient {
    constructor(config) {
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            config.tokenRefreshFn = tokenHandler.getTokenFn();
        }
        super({
            ...config,
            baseURL: config.baseURL || RETURNS_BASE_URL,
            authEngine: config.authEngine,
        });
        /* eslint-disable @typescript-eslint/explicit-function-return-type */
        /* eslint-disable @typescript-eslint/no-explicit-any */
        this.handleReturnError = (error) => {
            throw this.maybeGetDeliverrError(error) || this.maybeGetNetworkError(error) || this.getUnknownError(error);
        };
    }
    async getReturnsByOrderId(orderId) {
        return this.get(`/v1/orders/${orderId}/returns`).catch(this.handleReturnError);
    }
    async getReturnedItemsByOrderId(orderId) {
        return this.get(`/v1/orders/${orderId}/returned-items`).catch(this.handleReturnError);
    }
    async getDisposalSummary(sellerId) {
        return this.get(`/v1/returns/disposal-summary`, { sellerId }).catch(this.handleReturnError);
    }
    async getSellerInfo(sellerId, includeDispositions = false) {
        return this.get(`/v1/sellers/${sellerId}`, {
            includeDispositions: includeDispositions.toString(),
        }).catch(this.handleReturnError);
    }
    async updateSellerInfo(sellerId, seller) {
        return this.put(`/v1/sellers/${sellerId}`, seller).catch(this.handleReturnError);
    }
    async createSellerInfo(seller) {
        return this.post(`/v1/sellers`, seller).catch(this.handleReturnError);
    }
    async getShippingRatesV2(shippingRateRequest) {
        return this.post(`/v1/shipping/ratesV2`, shippingRateRequest).catch(this.handleReturnError);
    }
    async shareLabel(shareLabelRequest) {
        return this.post(`/v1/shipping/shareLabel`, shareLabelRequest).catch(this.handleReturnError);
    }
    async updateSellerDisposition(sellerId, sellerDispositionUpdateData) {
        return this.put(`/v1/sellers/${sellerId}/dispositions`, sellerDispositionUpdateData).catch(this.handleReturnError);
    }
    async updateSellerDispositionsBulk(sellerId, sellerDispositionBulkUpdateData) {
        return this.put(`/v1/sellers/${sellerId}/dispositions/bulk`, sellerDispositionBulkUpdateData).catch(this.handleReturnError);
    }
    async getRmtsBySellerId(sellerId) {
        return this.get(`/v1/sellers/${sellerId}/rmts/`).catch(this.handleReturnError);
    }
    async getSellerReturnProgram(sellerId) {
        return this.get(`/v1/sellers/${sellerId}/program`).catch(this.handleReturnError);
    }
    async addNewRmtBySellerId(sellerId, sellerRmtData, rmtId) {
        return this.post(`/v1/sellers/${sellerId}/rmts/${rmtId}`, sellerRmtData).catch(this.handleReturnError);
    }
    async updateRmtInfoBySellerIdAndRmtId(sellerId, sellerRmtUpdateData, rmtId) {
        return this.put(`/v1/sellers/${sellerId}/rmts/${rmtId}`, sellerRmtUpdateData).catch(this.handleReturnError);
    }
    async getSopById(sopId, includeConditionDetails = false) {
        return this.get(`/v1/sops/${sopId}`, {
            includeConditionDetails: includeConditionDetails.toString(),
        }).catch(this.handleReturnError);
    }
    async getReturns(query, perPage, currentPage, filterStatuses, filterTypes) {
        return this.get("/v1/returns", {
            q: query,
            limit: perPage,
            page: currentPage,
            filterStatuses,
            filterTypes,
        }).catch(this.handleReturnError);
    }
    async getReturnById(returnId) {
        return this.get(`/v1/returns/${returnId}`).catch(this.handleReturnError);
    }
    async createReturn(returnCreationRequest) {
        return this.post(`/v1/returns/`, returnCreationRequest).catch(this.handleReturnError);
    }
    async cancelReturn(returnId) {
        return this.put(`/v1/returns/${returnId}/cancel`, {}).catch(this.handleReturnError);
    }
    async markReturnArrived(returnId, markReturnArrivedRequest) {
        return this.put(`/v1/returns/${returnId}/arrived`, markReturnArrivedRequest).catch(this.handleReturnError);
    }
    async getReturnByTrackingCode(trackingCode) {
        return this.get(`/v1/tracking/${trackingCode}/returns`).catch(this.handleReturnError);
    }
    async createUnclaimedBlindReturn(minimalUnclaimedReturnCreationRequest) {
        return this.post(`/v1/returns/minimal_blind`, minimalUnclaimedReturnCreationRequest).catch(this.handleReturnError);
    }
    async createRTSReturn(minimalRTSReturnCreationRequest) {
        return this.post(`/v1/returns/rts`, minimalRTSReturnCreationRequest).catch(this.handleReturnError);
    }
    async createUnclaimedRTSReturn(minimalUnclaimedReturnCreationRequest) {
        return this.post(`/v1/returns/unclaimed_rts`, minimalUnclaimedReturnCreationRequest).catch(this.handleReturnError);
    }
    async createParcelReturn(minimalParcelReturnCreationRequest) {
        return this.post(`/v1/returns/parcel`, minimalParcelReturnCreationRequest).catch(this.handleReturnError);
    }
    async markReturnProcessed(returnId) {
        return this.post(`/v1/returns/${returnId}/inspection/processed`, undefined).catch(this.handleReturnError);
    }
    async printBarcodePdf(printBarcodeRequest) {
        const response = await this.request({
            data: printBarcodeRequest,
            method: "POST",
            responseType: "blob",
            url: `/v1/barcode/print`,
        }).catch(this.handleReturnError);
        return response.data;
    }
    async printBarcodeZpl(printBarcodeRequest) {
        return this.post(`/v1/barcode/print`, printBarcodeRequest).catch(this.handleReturnError);
    }
    async createEmptyPackage(createRestockingPackageRequest) {
        return this.post(`/v1/restock/packages`, createRestockingPackageRequest).catch(this.handleReturnError);
    }
    async addReturnOrderToPackage(barcodeOrPackageId, createQcPackageReturnOrder) {
        return this.post(`/v1/restock/packages/${barcodeOrPackageId}/return_order`, createQcPackageReturnOrder).catch(this.handleReturnError);
    }
    async dispatchPackage(barcodeOrPackageId) {
        return this.put(`/v1/restock/packages/${barcodeOrPackageId}/dispatch`, {}).catch(this.handleReturnError);
    }
    async disposePackage(barcodeOrPackageId) {
        return this.put(`/v1/restock/packages/${barcodeOrPackageId}/dispose`, {}).catch(this.handleReturnError);
    }
    async getPackageInfo(barcodeOrPackageId) {
        return this.get(`/v1/restock/packages/${barcodeOrPackageId}`).catch(this.handleReturnError);
    }
    async closePackage(barcodeOrPackageId) {
        return this.put(`/v1/restock/packages/${barcodeOrPackageId}/close`, {}).catch(this.handleReturnError);
    }
    async purchasePackageLabel(barcodeOrPackageId, purchaseQcPackageLabelRequest) {
        return this.post(`/v1/restock/packages/${barcodeOrPackageId}/purchase-label`, purchaseQcPackageLabelRequest).catch(this.handleReturnError);
    }
    async purchaseRTMPackageLabel(packageId, purchaseRTMPackageLabelRequest, sellerId) {
        return this.post(`/v1/packages/${packageId}/purchase-label`, purchaseRTMPackageLabelRequest, { sellerId }).catch(this.handleReturnError);
    }
    async saveImageBase64(createImageRequest) {
        return this.post(`/v1/images/base64`, createImageRequest).catch(this.handleReturnError);
    }
    async saveReturnInspectionNotes(returnId, inspectionNotes) {
        return this.put(`/v1/returns/${returnId}/notes`, {
            inspectionNotes,
        }).catch(this.handleReturnError);
    }
    async startReturnInspection(identifier, facilityId) {
        return this.post("/v2/qc/inspection", {
            identifier,
            facilityId,
        }).catch(this.handleReturnError);
    }
    async startReturnItemInspection(returnId, identifier, facilityId) {
        return this.post(`/v2/qc/inspection/${returnId}`, {
            identifier,
            facilityId,
        }).catch(this.handleReturnError);
    }
    async finishReturnItemInspection(returnId, itemInspectionRequest) {
        return this.post(`/v2/qc/inspection/${returnId}/items`, itemInspectionRequest).catch(this.handleReturnError);
    }
    async getProductsByDskus(dskus) {
        return this.post(`/v1/qc/get-products`, dskus).catch(this.handleReturnError);
    }
    async closeAndPurchaseRTMLabel(packageId, sellerId) {
        return this.put(`/v1/packages/${packageId}/close-and-purchase-label`, {}, {
            sellerId,
        }).catch(this.handleReturnError);
    }
    async getRTMPackageLabelRates(packageId, sellerId) {
        return this.get(`/v1/packages/${packageId}/label-rates`, {
            sellerId,
        }).catch(this.handleReturnError);
    }
    async getPackageDetails(packageId) {
        return this.get(`/v1/packages/${packageId}/details`).catch(this.handleReturnError);
    }
    async getPackagesForSeller(perPage, currentPage, boxStatuses, shipmentStatuses, sellerId) {
        return this.get("/v1/packages", {
            limit: perPage,
            page: currentPage,
            boxStatuses,
            shipmentStatuses,
            sellerId,
        }).catch(this.handleReturnError);
    }
}
exports.ReturnsClient = ReturnsClient;
//# sourceMappingURL=ReturnsClient.js.map